/**
 * <mango-job-brief> — the field brief for a scheduled work order.
 * =====================================================================
 * Retrieves the work order and exposes the two scheduled-stage transitions:
 *   • schedule → confirm pickup (stamps pickup_scheduled_at)
 *   • onsite   → arrive on site (stamps onsite_arrived_at; sends staff_count)
 *
 * Arrive On Site is gated on a staff count (the prototype's gate); the component
 * builds the typed onsite body { staff_count } from the input. The richer
 * onsite-scope capture (notes, client sign-off) happens on the Onsite screen
 * (data-action="complete-onsite").
 *
 * fmv_2 NEVER appears: the WorkOrder type has no such field; no bind references it.
 */
import { getWorkOrder, type WorkOrder, type OnsiteBody } from "../api/client";
import { applyBindings, wireActions } from "../bind";
import { loadView, date, setText, toggle, timeOfDay } from "./shared";

const VIEW_URL = new URL("../../views/job-brief.html", import.meta.url).href;

export class MangoJobBrief extends HTMLElement {
  static observedAttributes = ["data-short-name"];
  private disposeActions: (() => void) | null = null;
  private job: WorkOrder | null = null;

  async connectedCallback(): Promise<void> {
    this.innerHTML = await loadView(VIEW_URL);

    this.disposeActions = wireActions(this, {
      getBody: (ev) => {
        if (ev.action === "onsite") {
          const raw = this.querySelector<HTMLInputElement>("[data-staff-count]")?.value ?? "";
          const staff = Number.parseInt(raw, 10);
          if (!Number.isFinite(staff) || staff < 1) {
            this.setStatus("Enter the number of staff on site before logging arrival.");
            return false;
          }
          const body: Partial<OnsiteBody> = { staff_count: staff };
          return body;
        }
        return undefined; // schedule takes no body
      },
      onSuccess: () => {
        // Arriving doesn't navigate — reload so the brief flips to its "arrived"
        // state (Arrived at … + Start onsite scope). The scope form is reached
        // from there, not auto-opened.
        void this.load();
      },
      onError: (ev, error) => {
        console.error(`[mango-job-brief] ${ev.action} failed for ${ev.id}`, error);
        this.setStatus(`Could not ${ev.action} this work order. Please retry.`);
      },
    });

    this.wireStaffGate();
    await this.load();
  }

  disconnectedCallback(): void {
    this.disposeActions?.();
    this.disposeActions = null;
  }

  attributeChangedCallback(): void {
    if (this.isConnected) void this.load();
  }

  private get shortName(): string {
    return this.getAttribute("data-short-name") ?? "";
  }

  private setStatus(message: string): void {
    setText(this, "[data-status]", message);
  }

  /** Enable Arrive On Site only once a staff count >= 1 is entered. */
  private wireStaffGate(): void {
    const input = this.querySelector<HTMLInputElement>("[data-staff-count]");
    const btn = this.querySelector<HTMLButtonElement>("[data-arrive-btn]");
    input?.addEventListener("input", () => {
      const n = Number.parseInt(input.value, 10);
      if (btn) btn.disabled = !(Number.isFinite(n) && n >= 1);
    });
  }

  private async load(): Promise<void> {
    const id = this.shortName;
    if (!id) {
      this.setStatus("No work order specified.");
      return;
    }
    this.setStatus("Loading work order…");
    try {
      this.job = await getWorkOrder(id);
    } catch (error) {
      console.error("[mango-job-brief] failed to load", error);
      this.setStatus("Could not load this work order.");
      return;
    }
    this.render();
  }

  private render(): void {
    const job = this.job;
    if (!job) return;
    applyBindings(this, { job });

    const fmt = (field: string, value: string | null): void => {
      this.querySelectorAll<HTMLElement>(`[data-bind="job.${field}"]`).forEach(
        (el) => (el.textContent = date(value)),
      );
    };
    fmt("dropoff_datetime", job.dropoff_datetime);
    fmt("accepted_at", job.accepted_at);

    // Site & access — revealed post-acceptance (the brief is only reached when
    // scheduled+, so these are populated; "—" as a defensive fallback).
    setText(this, '[data-bind="job.pickup_address_street"]', job.pickup_address_street || "—");
    setText(this, '[data-bind="job.pickup_address_line2"]', job.pickup_address_line2 || "");
    setText(this, '[data-bind="job.pickup_contact_name"]', job.pickup_contact_name || "—");
    this.wireCall(job.pickup_contact_info);

    // Work order details.
    setText(this, '[data-bind="job.staff_count"]', job.staff_count == null ? "Not yet logged" : String(job.staff_count));
    setText(this, '[data-bind="job.onsite_arrived_at"]', job.onsite_arrived_at ? timeOfDay(job.onsite_arrived_at) : "Not yet");

    // Action area: SCHEDULED shows the arrive gate; ONSITE (arrived) shows the
    // arrival stamp + a "Start onsite scope" link to the completion form.
    const isOnsite = job.provider_status === "onsite";
    toggle(this, "[data-arrive-area]", !isOnsite);
    toggle(this, "[data-arrived-area]", isOnsite);
    if (isOnsite) {
      setText(this, "[data-arrived-label]",
        job.onsite_arrived_at ? `Arrived at ${timeOfDay(job.onsite_arrived_at)}` : "Arrived");
      const start = this.querySelector<HTMLAnchorElement>("[data-start-scope]");
      if (start) start.href = `#/onsite/${encodeURIComponent(job.short_name)}`;
    }

    this.setStatus("");
  }

  /** Point the Call button at the contact's phone (tel:), or hide it if absent. */
  private wireCall(contactInfo: string | null | undefined): void {
    const btn = this.querySelector<HTMLAnchorElement>("[data-call-btn]");
    if (!btn) return;
    const digits = (contactInfo ?? "").replace(/[^\d+]/g, "");
    if (digits) {
      btn.href = `tel:${digits}`;
      btn.style.display = "";
    } else {
      btn.style.display = "none";
    }
  }
}

export function registerMangoJobBrief(): void {
  if (!customElements.get("mango-job-brief")) {
    customElements.define("mango-job-brief", MangoJobBrief);
  }
}
